// 3rd party libs
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Components
import { DateButtonWithDropdown, DateRangeProps } from 'components';

import { MaintenanceQueryTable } from './MaintenanceQueryTable';
import { MaintenanceScheduleTable } from './MaintenanceScheduleTable';

// Types
import { Machine } from 'types';
import {
  MaintenanceCreator,
  MaintenanceEvent,
  MaintenanceEventArgs,
  MaintenanceEventStatus,
  MaintenanceEventTableRow,
  MaintenanceFrequencyType,
  MaintenanceScheduleArgs,
  MaintenanceScheduleTableRow
} from 'types/maintenance';

//Helpers
import { getShortDate, toISO8601 } from 'common/helpers/v1/dates';
import {
  MaintenanceSchedulePageProps,
  SecondaryTabViews,
  TabViews
} from './MaintenanceServiceDashboardContents';
import { MaintenanceRunTimeBasedQueryTable } from './MaintenanceQueryTableByFrequency';
// import { RunCycle } from './RunCycle';
import CreateServiceBtn from 'components/MaintenanceTable/CreateServiceBtn';
import PmPlanningTable from './PmPlanningTable';
import MachineReportsTable from './MachineReportsTable';
import { ServiceReportParams } from 'types/parts';
import { DataNotFound } from 'components/DataNotFound';

const RangeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
`;

// const RunCycleContainer = styled.div``;

const RunTimeContainer = styled.div`
  position: relative;
`;
const DatePickerContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
`;
const DateRangeLabel = styled.div`
  margin-right: 0.625rem;
  padding-top: 0.625rem;
`;

export const TableFilterContainer = styled.div<{ mb?: string; padding?: string }>`
  display: flex;
  padding: ${({ padding }) => padding || '0.5rem'};
  font-size: 0.875rem;
  justify-content: space-between;
  margin-bottom: ${({ mb }) => mb || '1.5rem'};
  align-items: center;
  box-shadow: 0px 1px 1px 0px ${(props) => props.theme.colors.lightBlack};
`;

interface Props {
  filter: MaintenanceEventArgs | MaintenanceScheduleArgs;
  machines?: Machine[];
  setNumResults: (num: number | null) => void;
  setSelectedPm: (pm: MaintenanceEventTableRow | MaintenanceScheduleTableRow) => void;
  secondaryTab: {
    value: SecondaryTabViews;
    selected: boolean;
  }[];
  primaryTab: TabViews;
  onClickCreateService?: (data: MaintenanceEventTableRow) => void;
  updateCompletedEvents?: (cm: MaintenanceScheduleTableRow[]) => void;
  setCurrentMaintenanceSchedulePage?: React.Dispatch<
    React.SetStateAction<MaintenanceSchedulePageProps | undefined>
  >;
  handleCreateServiceModal?: React.Dispatch<React.SetStateAction<boolean>>;
  handleTabChange?: (tab: SecondaryTabViews) => void;
  refetchEventOnUpdate?: number;
  isNewServiceEvent?: boolean;
  setIsNewServiceEvent?: React.Dispatch<React.SetStateAction<boolean>>;
  isDownloaded?: boolean;
}

export type FetchingState = {
  runHours: boolean;
  cycles: boolean;
  sip: boolean;
};
export interface GroupedEvents extends FetchingState {
  events: MaintenanceEvent[][];
}

const getRunTimeBasedFilter = (
  dateRange: DateRangeProps,
  maintenancesFrequencyType?: MaintenanceFrequencyType[]
): MaintenanceEventArgs => {
  if (dateRange.startTime.toString() === dateRange.endTime.toString()) {
    // default state before date selection
    return {
      status: MaintenanceEventStatus.NotComplete,
      ...(maintenancesFrequencyType && { frequencyType: maintenancesFrequencyType }),
      includeGroups: true,
      withoutPagination: true
    };
  }
  return {
    ...(dateRange.startTime && { dateAfter: toISO8601(getShortDate(dateRange.startTime)) }),
    ...(dateRange.endTime && { dateBefore: toISO8601(getShortDate(dateRange.endTime)) }),
    status: MaintenanceEventStatus.NotComplete,
    ...(maintenancesFrequencyType && { frequencyType: maintenancesFrequencyType }),
    includeGroups: true,
    withoutPagination: true
  };
};

const TopView = ({
  filter,
  machines,
  setNumResults,
  setSelectedPm,
  secondaryTab,
  primaryTab,
  onClickCreateService,
  updateCompletedEvents,
  setCurrentMaintenanceSchedulePage,
  handleCreateServiceModal,
  handleTabChange,
  refetchEventOnUpdate,
  isNewServiceEvent,
  setIsNewServiceEvent,
  isDownloaded,
}: Props): JSX.Element => {
  const theme = useTheme();
  const [currentFilter, setCurrentFilter] = useState<
    MaintenanceEventArgs | MaintenanceScheduleArgs
  >();
  const [cyclesFilter, setCyclesFilter] = useState<MaintenanceEventArgs | null>(null);
  const [runHoursFilter, setRunHoursFilter] = useState<MaintenanceEventArgs | null>(null);
  const [sipFilter, setSipFilter] = useState<MaintenanceEventArgs | null>(null);
  const [currentTab, setCurrentTab] = useState<SecondaryTabViews | number>();
  const [allGroupedEvents, setAllGroupedEvents] = useState<GroupedEvents>({
    events: [],
    runHours: true,
    cycles: false,
    sip: false
  });

  // const [createServiceModal, setCreateServiceModal ] = useState(false)
  const [allRunTimeEvents, setAllRunTimeEvents] = useState<MaintenanceEvent[]>([]);
  const { t } = useTranslation(['fpns']);
  //default date range:
  //start: 1 month before current date
  //end: 10 years after current date
  const [dateRange, setDateRangeState] = useState<DateRangeProps>({
    startTime: new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000),
    endTime: new Date(new Date().getTime() + 3650 * 24 * 60 * 60 * 1000)
  });
  const predictiveFilter: MaintenanceEventArgs =
    secondaryTab[SecondaryTabViews.Predictive]?.selected && machines && machines.length > 0
      ? {
          creator: MaintenanceCreator.Predictive,
          status: MaintenanceEventStatus.NotComplete
        }
      : {};

  const serviceTechFilter: MaintenanceEventArgs =
    secondaryTab[SecondaryTabViews.ServiceTech]?.selected && machines && machines.length > 0
      ? {
          creator: MaintenanceCreator.User,
          status: MaintenanceEventStatus.NotComplete
        }
      : {};

  const runTimeBasedCyclesFilter: MaintenanceEventArgs =
    secondaryTab[SecondaryTabViews.RunTimeBased]?.selected && machines && machines.length > 0
      ? getRunTimeBasedFilter(dateRange, [MaintenanceFrequencyType.Cycles])
      : {};
  const runTimeBasedRunHoursFilter: MaintenanceEventArgs =
    secondaryTab[SecondaryTabViews.RunTimeBased]?.selected && machines && machines.length > 0
      ? getRunTimeBasedFilter(dateRange, [MaintenanceFrequencyType.RunHours])
      : {};
  const runTimeBasedSipFilter: MaintenanceEventArgs =
    secondaryTab[SecondaryTabViews.RunTimeBased]?.selected && machines && machines.length > 0
      ? getRunTimeBasedFilter(dateRange, [MaintenanceFrequencyType.Sip])
      : {};

  const resolvedFilter: MaintenanceEventArgs = {
    status: MaintenanceEventStatus.Completed
  };

  const dailyFilter: MaintenanceScheduleArgs = {
    frequencyType: MaintenanceFrequencyType.Days,
    minimumFrequency: 1,
    maximumFrequency: 2
  };

  const weeklyFilter: MaintenanceScheduleArgs = {
    frequencyType: MaintenanceFrequencyType.Days,
    minimumFrequency: 7,
    maximumFrequency: 14
  };

  const monthlyFilter: MaintenanceScheduleArgs = {
    frequencyType: MaintenanceFrequencyType.Days,
    minimumFrequency: 28,
    maximumFrequency: 31
  };

  const quarterlyFilter: MaintenanceScheduleArgs = {
    frequencyType: MaintenanceFrequencyType.Days,
    minimumFrequency: 90,
    maximumFrequency: 90
  };
  const halfYearlyFilter: MaintenanceScheduleArgs = {
    frequencyType: MaintenanceFrequencyType.Days,
    minimumFrequency: 180,
    maximumFrequency: 182
  };
  const yearlyFilter: MaintenanceScheduleArgs = {
    frequencyType: MaintenanceFrequencyType.Days,
    minimumFrequency: 350,
    // Set this to undefined to capture anything greater than a year as well
    maximumFrequency: undefined
  };

  const othersTaskFilter: MaintenanceScheduleArgs = {
    frequencyType: undefined,
    minimumFrequency: -1,
    maximumFrequency: -1
  };

  useEffect(() => {
    secondaryTab.forEach((tab) => {
      if (tab.value == SecondaryTabViews.Predictive && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceEventArgs), ...predictiveFilter });
        setCurrentTab(SecondaryTabViews.Predictive);
      } else if (tab.value == SecondaryTabViews.RunTimeBased && tab.selected) {
        setCyclesFilter({
          ...(filter as MaintenanceEventArgs),
          ...runTimeBasedCyclesFilter
        });
        setRunHoursFilter({
          ...(filter as MaintenanceEventArgs),
          ...runTimeBasedRunHoursFilter
        });
        setSipFilter({
          ...(filter as MaintenanceEventArgs),
          ...runTimeBasedSipFilter
        });
        setAllRunTimeEvents([]);
        setCurrentTab(SecondaryTabViews.RunTimeBased);
      } else if (tab.value == SecondaryTabViews.ServiceTech && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceEventArgs), ...serviceTechFilter });
        setCurrentTab(SecondaryTabViews.ServiceTech);
      } else if (tab.value == SecondaryTabViews.Resolved && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceEventArgs), ...resolvedFilter });
        setCurrentTab(SecondaryTabViews.Resolved);
      } else if (tab.value == SecondaryTabViews.Daily && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...dailyFilter });
        setCurrentTab(SecondaryTabViews.Daily);
      } else if (tab.value == SecondaryTabViews.Weekly && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...weeklyFilter });
        setCurrentTab(SecondaryTabViews.Weekly);
      } else if (tab.value == SecondaryTabViews.Monthly && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...monthlyFilter });
        setCurrentTab(SecondaryTabViews.Monthly);
      } else if (tab.value == SecondaryTabViews.Quarterly && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...quarterlyFilter });
        setCurrentTab(SecondaryTabViews.Quarterly);
      } else if (tab.value == SecondaryTabViews.HalfYearly && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...halfYearlyFilter });
        setCurrentTab(SecondaryTabViews.HalfYearly);
      } else if (tab.value == SecondaryTabViews.Yearly && tab.selected) {
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...yearlyFilter });
        setCurrentTab(SecondaryTabViews.Yearly);
      } else if (tab.value === SecondaryTabViews.PmPlanning && tab.selected) {
        setCurrentFilter({ ...filter, creator: MaintenanceCreator.Inspection });
        setCurrentTab(SecondaryTabViews.PmPlanning);
      } else if (tab.value === SecondaryTabViews.Reports && tab.selected) {
        setCurrentFilter({ ...filter, creator: MaintenanceCreator.Inspection });
        setCurrentTab(SecondaryTabViews.Reports);
      } else if(tab.value === SecondaryTabViews.OtherTasks && tab.selected){        
        setCurrentFilter({ ...(filter as MaintenanceScheduleArgs), ...othersTaskFilter });
        setCurrentTab(SecondaryTabViews.OtherTasks);
      }
    });
  }, [secondaryTab, filter, SecondaryTabViews.PmPlanning, t]);

  const setDateRange = (range: DateRangeProps): void => {
    setDateRangeState(range);
    setCyclesFilter({
      ...(filter as MaintenanceEventArgs),
      ...getRunTimeBasedFilter(range, [MaintenanceFrequencyType.Cycles])
    });
    setRunHoursFilter({
      ...(filter as MaintenanceEventArgs),
      ...getRunTimeBasedFilter(range, [MaintenanceFrequencyType.RunHours])
    });
    setSipFilter({
      ...(filter as MaintenanceEventArgs),
      ...getRunTimeBasedFilter(range, [MaintenanceFrequencyType.Sip])
    });
  };

  const handleActionClick = () => {
    handleTabChange && handleTabChange(SecondaryTabViews.RunTimeBased);
  };
  return (
    <>
      {currentTab == SecondaryTabViews.RunTimeBased && (
        <RunTimeContainer>
          <TableFilterContainer>
            <CreateServiceBtn handleServiceModal={handleCreateServiceModal} />
            {/* {allRunTimeEvents.length > 0 && (
              <RunCycleContainer>
                <RunCycle allEvents={allRunTimeEvents} machines={machines} />
              </RunCycleContainer>
            )} */}
            <RangeContainer>
              <DatePickerContainer className="date-picker-wrapper">
                <DateRangeLabel>{t('filter_by_date')}</DateRangeLabel>
                <DateButtonWithDropdown
                  hideTimeInputs={true}
                  hideDateRangeSelect={true}
                  hideTimeRangeSelect={true}
                  hasFutureDates
                  hasGoBackDateLimit={300}
                  {...{ dateRange, setDateRange }}
                />
              </DatePickerContainer>
            </RangeContainer>
          </TableFilterContainer>
          <MaintenanceRunTimeBasedQueryTable
            filter={cyclesFilter}
            headerBgColor={theme.colors.lightGrey1}
            machines={machines}
            setNumResults={setNumResults}
            setSelectedPm={setSelectedPm}
            allEvents={allRunTimeEvents}
            setAllRunTimeEvents={setAllRunTimeEvents}
            allGroupedEvents={allGroupedEvents}
            setAllGroupedEvents={setAllGroupedEvents}
            eventType={'cycles'}
            refetchEventOnUpdate={refetchEventOnUpdate}
          />
          <MaintenanceRunTimeBasedQueryTable
            filter={runHoursFilter}
            headerBgColor={theme.colors.lightGrey1}
            machines={machines}
            setNumResults={setNumResults}
            setSelectedPm={setSelectedPm}
            allEvents={allRunTimeEvents}
            setAllRunTimeEvents={setAllRunTimeEvents}
            allGroupedEvents={allGroupedEvents}
            setAllGroupedEvents={setAllGroupedEvents}
            eventType={'runHours'}
            refetchEventOnUpdate={refetchEventOnUpdate}
          />
          <MaintenanceRunTimeBasedQueryTable
            filter={sipFilter}
            headerBgColor={theme.colors.lightGrey1}
            machines={machines}
            setNumResults={setNumResults}
            setSelectedPm={setSelectedPm}
            allEvents={allRunTimeEvents}
            setAllRunTimeEvents={setAllRunTimeEvents}
            allGroupedEvents={allGroupedEvents}
            setAllGroupedEvents={setAllGroupedEvents}
            eventType={'sip'}
            refetchEventOnUpdate={refetchEventOnUpdate}
          />
        </RunTimeContainer>
      )}
      {!allGroupedEvents.events.length &&
        currentTab === SecondaryTabViews.RunTimeBased &&
        !allGroupedEvents.cycles &&
        !allGroupedEvents.runHours &&
        !allGroupedEvents.sip && <DataNotFound textMsg={t('nothing_to_show') || ''} />}
      {currentTab === SecondaryTabViews.PmPlanning && (
        <PmPlanningTable
          filter={currentFilter as MaintenanceEventArgs}
          machines={machines}
          handleServiceModal={handleCreateServiceModal}
          handleActionClick={handleActionClick}
          setNumResults={setNumResults}
        />
      )}
      {currentTab === SecondaryTabViews.Reports && (
        <MachineReportsTable filter={currentFilter as ServiceReportParams} />
      )}
      {currentFilter &&
        primaryTab != TabViews.Planned &&
        currentTab != SecondaryTabViews.RunTimeBased &&
        currentTab !== SecondaryTabViews.PmPlanning &&
        currentTab !== SecondaryTabViews.Reports && (
          <MaintenanceQueryTable
            filter={currentFilter as MaintenanceEventArgs}
            headerBgColor={theme.colors.lightGrey1}
            machines={machines}
            setNumResults={setNumResults}
            setSelectedPm={setSelectedPm}
            showSecondDescription={currentTab == SecondaryTabViews.Predictive ? false : true}
            onClickCreateService={onClickCreateService}
            handleServiceModal={handleCreateServiceModal}
            isNewEvent={isNewServiceEvent}
            setIsNewEvent={setIsNewServiceEvent}
            isServiceTab={currentTab == SecondaryTabViews.ServiceTech}
          />
        )}
      {primaryTab == TabViews.Planned && (
        <MaintenanceScheduleTable
          filter={currentFilter as MaintenanceScheduleArgs}
          headerBgColor={theme.colors.lightGrey1}
          machines={machines}
          setNumResults={setNumResults}
          setSelectedPm={setSelectedPm}
          setCompletedEvents={updateCompletedEvents}
          selectedSecondaryTab={currentTab}
          isDownloaded={isDownloaded}
          setCurrentMaintenanceSchedulePage={setCurrentMaintenanceSchedulePage}
        />
      )}
    </>
  );
};

export default React.memo(TopView);
